import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { BREAKPOINTS_XSMALL, VIDEO_TYPE_LIST } from '@app-core/constants/constants';
import { DataService } from '@app-core/services/data/data.service';
import { DateService } from '@app-core/services/date/date.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { UpdateDurationFilter } from '@app-dvr/actions/duration-filter.actions';
import { FindTripComponent } from '@app-dvr/components/find-trip/find-trip.component';
import { getDurationFilterState, State } from '@app-dvr/reducers';
import { DvrService } from '@app-dvr/services/dvr/dvr.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { ActivatedRoute, Router } from '@angular/router';
import { VideoRequestsPageEventType, VideoRequestsPageQueryParams } from '@app-dvr/common/dvr.model';

@Component({
  selector: 'app-dvr',
  templateUrl: './dvr.component.html',
  styleUrls: ['./dvr.component.scss'],
})
export class DvrComponent implements OnInit, OnDestroy {
  public videoTypeList = VIDEO_TYPE_LIST;
  public currentTabIndex = 0;
  public currentMetricUnit: string;
  public videoRequests = [];
  public startDate: string;
  public endDate: string;
  public fleetId: string;
  public selectedVideoRequestType = 'dvr';
  public videoRequestType = new FormControl('dvr');
  public loader = true;
  public isTouchDevice = false;

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private tagIds = [];

  constructor(
    public translate: TranslateService,
    private dialog: MatDialog,
    public dataService: DataService,
    private gtmService: GoogleTagManagerService,
    private store: Store<State>,
    private dvrService: DvrService,
    private dateService: DateService,
    private breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
    private router: Router,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit() {
    this.parseQueryParams();

    this.dataService.refreshIncidentsList$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.doRefresh();
    });

    this.breakpointObserver.observe(BREAKPOINTS_XSMALL).subscribe((state: BreakpointState) => {
      this.isTouchDevice = state.matches;
    });

    this.store
      .select(getDurationFilterState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((durationFilterState) => {
        ({ paramStartDate: this.startDate, paramEndDate: this.endDate, tagIds: this.tagIds } = durationFilterState);
        if (this.startDate && this.endDate && this.currentMetricUnit) {
          this.getData();
        }
      });

    this.dataService._currentMetricUnit.subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
        if (this.startDate && this.endDate && this.currentMetricUnit) {
          this.getData();
        }
      }
    });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
        this.getData();
      }
    });

    this.videoRequestType.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((requestType) => {
      this.selectedVideoRequestType = requestType;
      let queryParamEventType: VideoRequestsPageEventType;
      if (requestType === 'edvr') {
        queryParamEventType = VideoRequestsPageEventType.EDVR;
        this.gtmService.changeRequestVideoTypeFilter('Event On-Demand');
        this.mixpanelService.track('Event On-Demand sub-tab clicked in Video Requests Page', {
          'Request Type': 'Event On-Demand',
        });
        this.getFleetEdvrRequests();
      } else if (requestType === 'dvr') {
        queryParamEventType = VideoRequestsPageEventType.DVR;
        this.gtmService.changeRequestVideoTypeFilter('Video Request');
        this.mixpanelService.track('Video request sub-tab clicked in Video Requests Page', {
          'Request Type': 'Video Request',
        });
        this.getFleetDvrRequests();
      } else {
        queryParamEventType = VideoRequestsPageEventType.PANIC_BUTTON;
        this.gtmService.changeRequestVideoTypeFilter('Panic Button');
        this.mixpanelService.track('Panic button sub-tab clicked in Video Requests Page', {
          'Request Type': 'Panic Button',
        });
        this.getFleetExternalEvents();
      }
      this.updateQueryParams({ eventType: queryParamEventType });
    });
  }

  private parseQueryParams() {
    this.route.queryParams.pipe(take(1)).subscribe((params: VideoRequestsPageQueryParams) => {
      const { startTimeUTC, endTimeUTC, eventType, tripId, driverId, uploadRequestId, eventIndex } = params;
      if (eventType === VideoRequestsPageEventType.EDVR) {
        this.selectedVideoRequestType = 'edvr';
      } else if (eventType === VideoRequestsPageEventType.PANIC_BUTTON) {
        this.selectedVideoRequestType = 'externalEvents';
      } else {
        this.selectedVideoRequestType = 'dvr';
      }
      this.videoRequestType.patchValue(this.selectedVideoRequestType);

      if (tripId && driverId && (uploadRequestId || eventIndex)) {
        this.getEventDetailsAndOpenDialog(params);
      }

      let filterState: any = {};
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      const milliSeconds = now.getMilliseconds();

      if (startTimeUTC) {
        // Setting current Hours, minutes and seconds since datepicker only sets date part.
        const startDate = new Date(startTimeUTC);
        const from = this.dateService.toDaysStartISO(new Date(startDate.setHours(hours, minutes, seconds, milliSeconds)));
        filterState = {
          ...filterState,
          displayStartDate: startDate,
          paramStartDate: from,
          days: 'custom',
        };
      }

      if (endTimeUTC) {
        // Setting current Hours, minutes and seconds since datepicker only sets date part.
        const endDate = new Date(endTimeUTC);
        const to = this.dateService.toDaysEndISOPlusOne(new Date(endDate.setHours(hours, minutes, seconds, milliSeconds)));
        filterState = {
          ...filterState,
          displayEndDate: endDate,
          paramEndDate: to,
          days: 'custom',
        };
      }

      this.store.dispatch(new UpdateDurationFilter(filterState));
    });
  }

  private updateQueryParams(newQueryParams: VideoRequestsPageQueryParams) {
    this.router.navigate([], {
      queryParams: {
        ...newQueryParams,
        // set null to all single event related query params,
        tripId: null,
        driverId: null,
        eventIndex: null,
        uploadRequestId: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  public onVideoRequestsPageChange(event: PageEvent) {
    this.gtmService.videoRequestsPageChange(event);
  }

  public changeDuration(durationChange: any): void {
    const {
      paramStartDate: newStartDate = '',
      paramEndDate: newEndDate = '',
      displayStartDate = null,
      displayEndDate = null,
    } = durationChange || {};
    const oldStartDate = this.startDate;
    const oldEndDate = this.endDate;
    const isTimeRangeChanged = newStartDate !== oldStartDate || newEndDate !== oldEndDate;
    if (isTimeRangeChanged) {
      const noOfDays = this.dateService.getNoOfDays(newStartDate, newEndDate);
      const durationInText = this.dateService.getDurationText(displayStartDate, displayEndDate, noOfDays);
      this.gtmService.changeVideoRequestOverviewDurationTypeFilter(durationInText, noOfDays);
    }
    this.store.dispatch(new UpdateDurationFilter(durationChange));

    const newQueryParams: VideoRequestsPageQueryParams = {
      startTimeUTC: new Date(displayStartDate).toISOString(),
      endTimeUTC: new Date(displayEndDate).toISOString(),
    };
    this.updateQueryParams(newQueryParams);
  }

  public doRefresh() {
    if (this.videoRequestType.value === 'dvr') {
      this.getFleetDvrRequests(true);
    } else if (this.videoRequestType.value === 'edvr') {
      this.getFleetEdvrRequests(true);
    } else {
      this.getFleetExternalEvents(true);
    }
    this.mixpanelService.track('Clicked refresh - Video Requests Page');
  }

  public getData() {
    if (this.videoRequestType.value === 'dvr') {
      this.getFleetDvrRequests();
    } else if (this.videoRequestType.value === 'edvr') {
      this.getFleetEdvrRequests();
    } else {
      this.getFleetExternalEvents();
    }
  }

  public openDvrFormDialog() {
    this.dialog.open(FindTripComponent, {
      panelClass: ['incident-modal', 'mobile-modal'],
      position: { top: '24px' },
      width: '960px',
      minHeight: '480px',
      autoFocus: false,
    });
    this.mixpanelService.track('Find Trip');
  }

  public getFleetDvrRequests(isRefresh?: boolean) {
    this.loader = true;
    const params = {
      startDate: this.startDate,
      endDate: this.endDate,
      limit: 500,
      sort: 'desc',
    };
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    this.dvrService
      .getFleetDvrRequests(params, isRefresh)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any = {}) => {
          this.videoRequests = res.data.uploadRequests;
        },
        () => {
          this.videoRequests = [];
        }
      );
  }

  public getFleetEdvrRequests(isRefresh?: boolean) {
    this.loader = true;
    const params = {
      startDate: this.startDate,
      endDate: this.endDate,
      limit: 500,
      sort: 'desc',
    };
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    this.dvrService
      .getFleetEdvrRequests(params, isRefresh)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any = {}) => {
          this.videoRequests = res.data.edvrRequests;
        },
        () => {
          this.videoRequests = [];
        }
      );
  }

  public getFleetExternalEvents(isRefresh?: boolean) {
    this.loader = true;
    const params = {
      startDate: this.startDate,
      endDate: this.endDate,
      limit: 500,
      sort: 'desc',
    };
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    this.dvrService
      .getFleetExternalEvents(params, isRefresh)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any = {}) => {
          this.videoRequests = res.rows;
        },
        () => {
          this.videoRequests = [];
        }
      );
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getEventDetailsAndOpenDialog(params: VideoRequestsPageQueryParams) {
    this.dvrService.getEventDetailsAndOpenDialog(params).pipe(takeUntil(this.ngUnsubscribe)).subscribe();
  }
}
